import type {ItemObject, ItemType} from '@PosterWhiteboard/items/item/item.types';
import {openMessageModal} from '@Modals/message-modal';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {showMessageGrowl} from '@Components/message-growl/message-growl';
import {getUserId, isUserDesigner, isUserLoggedIn} from '@Libraries/user.library';
import {openErrorModal} from '@Modals/error-modal';
import {ITEM_CONTROL_DIMENSIONS} from '@PosterWhiteboard/poster/poster-item-controls';
import {GROWL_TYPE} from '@Components/message-growl';
import type React from 'react';
import {createItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import type {FabricPositionalParams} from '@Utils/fabric.util';
import {copyToClipboard, readFromClipboard} from '@Utils/clipboard.util';
import {ElementDataType, isTempImageData} from '@Libraries/add-media-library';
import {isJson} from '@Utils/string.util';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {ItemData} from './add-item.class';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import {Point} from '@postermywall/fabricjs-2';
import {DEFAULT_TEXT_FONT_FAMILY} from '@PosterWhiteboard/classes/text-styles.class';
import {getRecommendedFont, getScriptForText} from '@Libraries/font-library';
import {
  getMediaStorageForbackendData,
  getSingleUploadedMediaItem,
  type UploadFileDataTypes,
  uploadFiles,
  type UserMediaType,
  type UserMediaVOResponse,
  userMediaVOToElementData,
} from '@Libraries/user-media-library';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import {onAddItems} from '@Components/poster-editor/library/poster-editor-open-modals';
import {updateUnlinkedMediaState} from '@Panels/user-media-panel/user-media-panel-reducer';
import {POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';
import {makeShortcut} from '@Utils/shortcut.util';
import {isDesktop} from 'react-device-detect';
import {addQrCodeToPage} from '@PosterWhiteboard/items/qr-code-item.class';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import {getVisiblePageBounds} from '@PosterWhiteboard/page/page-visibility.util';
import type {PageGroupsObject} from '@PosterWhiteboard/page/page-groups.class';

export const PMW_CUSTOM_CLIPBOARD_MIMETYPE = 'web application/x-postermywall';
export const PLAIN_TEXT_CLIPBOARD_MIMETYPE = 'text/plain';

interface ClipboardPasteInfo {
  isPasteFromRightClick: boolean;
  eventCanvasOffset?: FabricPositionalParams;
}

interface PosterClipboardData {
  key: string;
  version: string;
  graphicItemObjects: ItemObject[];
  audioItemObject?: AudioItemObject;
  posterInfo: ClipboardPosterInfo;
  selection: Record<string, number | null>;
  groups?: PageGroupsObject;
}

interface ClipboardPosterInfo {
  width: number;
  height: number;
  idGalleryTemplate?: string;
  idGalleryTemplateCreator: number;
}

/**
 * Makes sure user isn't able to copy-paste items from an older version poster editor where item structure was different
 */
const PASTE_ITEMS_VERSION = `${POSTER_VERSION.CURRENT}_1`;

export class PageClipboard extends CommonMethods {
  public page: Page;

  public constructor(page: Page) {
    super();
    this.page = page;
  }

  public copySelection(e?: Event | React.MouseEvent<HTMLElement>): void {
    const selection = this.page.getSelectedItems();

    if (selection.length) {
      let graphicItems: ItemType[] = [];

      for (let i = 0; i < selection.length; i++) {
        const selectionItem = selection[i];
        graphicItems.push(selectionItem);
      }
      if (graphicItems.length) {
        if (e) {
          e.preventDefault(); // default behaviour is to copy any selected text
        }
        graphicItems = this.page.activeSelection.orderGraphicItems(graphicItems);

        const graphicItemObjects = graphicItems.map((item) => {
          return item.toObject();
        });

        const {poster} = this.page;
        const activeObject = this.page.activeSelection.getActiveObject();
        // activeObject is null when a single item is selected, so fall back to that item's own coordinates
        const copiedGroups: PageGroupsObject['groupsHashMap'] = {};
        for (const item of graphicItems) {
          if (!item.groupId || copiedGroups[item.groupId]) {
            continue;
          }
          const groupRecord = this.page.items.groups.groupsHashMap[item.groupId];
          if (groupRecord) {
            copiedGroups[item.groupId] = groupRecord;
          }
        }

        const graphicItemsAndPoster: PosterClipboardData = {
          key: poster.copyKey,
          graphicItemObjects,
          version: PASTE_ITEMS_VERSION,
          posterInfo: {
            width: poster.width,
            height: poster.height,
            idGalleryTemplate: this.page.galleryTemplateData?.posterId,
            idGalleryTemplateCreator: this.page.galleryTemplateData?.idGalleryTemplateCreator ?? 0,
          },
          selection: {
            left: activeObject ? activeObject.left : graphicItemObjects[0].left,
            top: activeObject ? activeObject.top : graphicItemObjects[0].top,
            width: activeObject ? activeObject.width : null,
            height: activeObject ? activeObject.height : null,
          },
          groups: {groupsHashMap: copiedGroups},
        };
        const jsonString = JSON.stringify(graphicItemsAndPoster);
        void copyToClipboard(jsonString);
      } else {
        openMessageModal({
          title: window.i18next.t('pmwjs_copying_error_title'),
          text: window.i18next.t('pmwjs_title_copy_disabled'),
        });
      }
    } else {
      void this.checkAndCopyAudioSelection();
    }
  }

  public async checkAndCopyAudioSelection(): Promise<void> {
    const audioItem = this.page.audioClips.getSelectedAudioItem();

    if (!audioItem) {
      return;
    }

    const {poster} = this.page;
    const jsonString = JSON.stringify({
      key: poster.copyKey,
      version: PASTE_ITEMS_VERSION,
      graphicItemObjects: [],
      audioItemObject: audioItem.toObject(),
      posterInfo: {
        width: poster.width,
        height: poster.height,
        idGalleryTemplate: poster.getGalleryTemplateId(),
        idGalleryTemplateCreator: poster.getGalleryTemplateCreatorId(),
      },
      selection: {},
    } as PosterClipboardData);
    await copyToClipboard(jsonString);
  }

  public async pasteInternalItemsAndPosterDataIfInClipboard(
    clipboardData: unknown,
    e?: Event | ClipboardEvent | React.MouseEvent<HTMLElement>,
    clipboardPasteInfo: ClipboardPasteInfo = {isPasteFromRightClick: false},
    onSuccess?: () => void
  ): Promise<void> {
    const {poster} = this.page;
    const graphicItemsAndPoster = await getItemsAndPosterDataFromClipboard(clipboardData);
    if (graphicItemsAndPoster && graphicItemsAndPoster.graphicItemObjects.length > 0) {
      if (this.isPasteAllowed(graphicItemsAndPoster)) {
        if (e) {
          e.preventDefault(); // We are already handling the data from the clipboard, we do not want it inserted into the document
        }
        showLoading('pastingGraphicItems');
        // if target poster has different dimensions than source poster paste the copied graphic items at (40,40) coordinate
        let deltaX = 15;
        let deltaY = 15;
        const arePosterDimensionsDifferent = poster.width !== graphicItemsAndPoster.posterInfo.width || poster.height !== graphicItemsAndPoster.posterInfo.height;
        const graphicItems: ItemType[] = [];
        const multipleGraphicItems = graphicItemsAndPoster.graphicItemObjects.length > 1;

        const graphicItemPromises = [];
        const graphicItemOrderIds = [];
        const visibleBounds = !isEditorMobileVariant() ? getVisiblePageBounds(this.page) : undefined;

        for (const item of graphicItemsAndPoster.graphicItemObjects) {
          graphicItemOrderIds.push(item.uid);
          graphicItemPromises.push(
            new Promise<void>((resolve, reject) => {
              createItemFromObject(this.page, item)
                .then((graphicItem) => {
                  const templateCreator = graphicItemsAndPoster.posterInfo.idGalleryTemplateCreator;
                  if (!this.isItemPasteAllowed(graphicItemsAndPoster.key, graphicItem.idOriginalOwner, templateCreator)) {
                    hideLoading('pastingGraphicItems');
                    openErrorModal({
                      title: window.i18next.t('pmwjs_pasting_error_title'),
                      message: window.i18next.t('pmwjs_item_pasting_error'),
                    });
                    reject(new Error('Item not alloed to be pasted'));
                    return;
                  }
                  if (graphicItemsAndPoster.posterInfo.idGalleryTemplate) {
                    graphicItem.idOriginalOwner = templateCreator;
                  }

                  // Only set coordinates of new graphic item if it's added through right click/touch event, and not keypress shortcut.
                  const eventOffsetX = clipboardPasteInfo.eventCanvasOffset?.left;
                  const eventOffsetY = clipboardPasteInfo.eventCanvasOffset?.top;

                  graphicItem.onPageStartTime = 0;
                  graphicItem.onPageEndTime = this.page.getDuration();

                  const resizeDimensions = poster.resizePoster.getDimensionsToResizePageItemsToTargetType(
                    graphicItemsAndPoster.posterInfo.width,
                    graphicItemsAndPoster.posterInfo.height,
                    graphicItem.toObject(),
                    {
                      width: poster.width,
                      height: poster.height,
                    }
                  );
                  if (!clipboardPasteInfo.isPasteFromRightClick) {
                    if (visibleBounds) {
                      const visibleWidth = visibleBounds.right - visibleBounds.left;
                      const visibleHeight = visibleBounds.bottom - visibleBounds.top;
                      const targetAnchorX = visibleBounds.left + visibleWidth / 10;
                      const targetAnchorY = visibleBounds.top + visibleHeight / 8;
                      const groupAnchorLeft = graphicItemsAndPoster.selection.left ?? 0;
                      const groupAnchorTop = graphicItemsAndPoster.selection.top ?? 0;
                      const relativeX = (graphicItem.fabricObject.getX() - groupAnchorLeft) * resizeDimensions.scaleFactor;
                      const relativeY = (graphicItem.fabricObject.getY() - groupAnchorTop) * resizeDimensions.scaleFactor;
                      graphicItem.fabricObject.setXY(new Point(targetAnchorX + relativeX, targetAnchorY + relativeY));
                    } else {
                      const newX = graphicItem.fabricObject.getX() + resizeDimensions.left;
                      const newY = graphicItem.fabricObject.getY() + resizeDimensions.top;
                      graphicItem.fabricObject.setXY(new Point(newX, newY));
                    }
                  } else {
                    if (
                      clipboardPasteInfo.isPasteFromRightClick &&
                      eventOffsetX !== undefined &&
                      eventOffsetY !== undefined &&
                      graphicItemsAndPoster.selection.left &&
                      graphicItemsAndPoster.selection.top
                    ) {
                      const scrollOffsetX = visibleBounds?.left ?? 0;
                      const scrollOffsetY = visibleBounds?.top ?? 0;
                      graphicItem.fabricObject.setXY(
                        new Point(
                          eventOffsetX + (graphicItem.fabricObject.getX() - graphicItemsAndPoster.selection.left) * resizeDimensions.scaleFactor + scrollOffsetX,
                          eventOffsetY + (graphicItem.fabricObject.getY() - graphicItemsAndPoster.selection.top) * resizeDimensions.scaleFactor + scrollOffsetY
                        )
                      );
                    }
                  }
                  graphicItem.fabricObject.scaleX *= resizeDimensions.scaleFactor;
                  graphicItem.fabricObject.scaleY *= resizeDimensions.scaleFactor;

                  graphicItems.push(graphicItem);

                  resolve();
                })
                .catch(reject);
            })
          );
        }

        const responses = await Promise.allSettled(graphicItemPromises);

        for (const response of responses) {
          if (response.status === 'rejected') {
            console.error('Item failed to paste details:', response);
          }
        }

        if (clipboardPasteInfo.isPasteFromRightClick) {
          deltaX = multipleGraphicItems ? ITEM_CONTROL_DIMENSIONS.PMW_CONTROL_PADDING : graphicItems[0].getSelectorPadding();
          deltaY = deltaX;
        }

        // Rearrange graphicItems to match the order of graphicItemOrderIds
        const itemMap = new Map<string, ItemType>();
        graphicItems.forEach((item) => {
          itemMap.set(item.uid, item);
        });

        const orderedGraphicItems: ItemType[] = [];
        graphicItemOrderIds.forEach((uid) => {
          const item = itemMap.get(uid);
          if (item) {
            orderedGraphicItems.push(item);
          }
        });

        this.page.activeSelection.duplicateSelection(orderedGraphicItems, {
          sourceGroupHashMap: graphicItemsAndPoster.groups?.groupsHashMap ?? {},
          x: deltaX,
          y: deltaY,
          arePosterDimensionsDifferent,
        });
        hideLoading('pastingGraphicItems');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        openMessageModal({
          title: window.i18next.t('pmwjs_pasting_error_title'),
          text: window.i18next.t('pmwjs_pasting_error'),
        });
      }
    }
  }

  public async pasteItems(
    e?: Event | ClipboardEvent | React.MouseEvent<HTMLElement>,
    clipboardPasteInfo: ClipboardPasteInfo = {isPasteFromRightClick: false},
    onSuccess?: () => void
  ): Promise<void> {
    let file: File | undefined;
    if (e instanceof ClipboardEvent && e.clipboardData?.files) {
      const filesArray = Array.from(e.clipboardData.files);
      file = filesArray.find((item: File) => item.type.includes('image'));
    }
    const clipboardData = await getDataFromClipboard();
    if (isItemsAndPosterClipboardDataValid(clipboardData)) {
      await this.pasteAudioItemIfInClipboard(clipboardData, e, onSuccess);
      await this.pasteInternalItemsAndPosterDataIfInClipboard(clipboardData, e, clipboardPasteInfo, onSuccess);
    } else {
      if (clipboardPasteInfo.isPasteFromRightClick) {
        const files = await this.getExternalCopiedimageFromRightClick();
        if (files === null) {
          return;
        }
        file = files.length > 0 ? files[0] : undefined;
      }
      if (file) {
        await this.pasteExternalImageItemIfInClipboard(file, onSuccess);
        return;
      } else if (await this.addedQRCodeItemFromClipboard()) {
        return;
      } else {
        const text = await getExternalTextItemDataFromClipboard(clipboardData as string);
        if (text) {
          this.pasteExternalTextItemIfInClipboard(text, e, onSuccess);
          return;
        }
      }
      showMessageGrowl({
        text: window.i18next.t('pmwjs_pasted_content_not_supported'),
        icon: 'icon-poster-print',
        type: GROWL_TYPE.DANGER,
        interval: 2000,
        showCloseIcon: true,
      });
    }
  }
  public async getExternalCopiedimageFromRightClick(): Promise<File[] | null> {
    try {
      const items = await navigator.clipboard.read();
      const allTypePromises = items.flatMap((item) =>
        item.types.map(async (type) => {
          const blob = await item.getType(type);
          if (blob.type.startsWith('image/')) {
            const extension = blob.type.split('/')[1] || 'png';
            return new File([blob], `pasted.${extension}`, {type: blob.type});
          }
          return null;
        })
      );

      return (await Promise.all(allTypePromises)).filter(Boolean) as File[];
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        showMessageGrowl({
          text: isDesktop
            ? window.i18next.t('pmwjs_clipboard_access_denied_with_shortcut', {shortcut: makeShortcut(['Ctrl', 'V'])})
            : window.i18next.t('pmwjs_clipboard_access_denied'),
          icon: 'icon-poster-print',
          type: GROWL_TYPE.DANGER,
          interval: 2000,
          showCloseIcon: true,
        });
      }
      return null;
    }
  }

  public async pasteExternalImageItemIfInClipboard(image: File | undefined, onSuccess?: () => void): Promise<void> {
    if (!image) {
      return;
    }
    const visibleBounds = !isEditorMobileVariant() ? getVisiblePageBounds(this.page) : null;
    await uploadFiles(
      {
        onFileUpload: (type: UserMediaType, data: UploadFileDataTypes): void => {
          const item = getSingleUploadedMediaItem(data);
          if (!window.posterEditor?.whiteboard?.getCurrentPage() || !item) {
            return;
          }
          if (isTempImageData(item)) {
            onAddItems([{...item, ...(visibleBounds && {top: visibleBounds.top, left: visibleBounds.left})}]);
          } else {
            if (!isUserLoggedIn()) {
              window.PMW.redux.store.dispatch(updateUnlinkedMediaState([getMediaStorageForbackendData(item, type)]));
            }
            const itemData = userMediaVOToElementData(type, item);
            onAddItems([{...itemData, ...(visibleBounds && {top: visibleBounds.top, left: visibleBounds.left})}]);
          }
        },
        onUploadError: onPastedImageUploadError,
        onAfterUpload: onAfterPastedImageUpload,
      },
      [image],
      {
        shouldLoadDataUrlImage: true,
      }
    );
    if (onSuccess) {
      onSuccess();
    }
  }

  public async addedQRCodeItemFromClipboard(): Promise<boolean> {
    const clipboardItems = await navigator.clipboard.read();
    const item = clipboardItems.find((item) => item.types.includes(PMW_CUSTOM_CLIPBOARD_MIMETYPE));
    if (item?.types.includes(PMW_CUSTOM_CLIPBOARD_MIMETYPE)) {
      const blob = await item.getType(PMW_CUSTOM_CLIPBOARD_MIMETYPE);
      const qrCode = await blob.text();
      const visibleBounds = !isEditorMobileVariant() ? getVisiblePageBounds(this.page) : null;
      addQrCodeToPage(qrCode, visibleBounds?.top, visibleBounds?.left);
      return true;
    }
    return false;
  }

  public pasteExternalTextItemIfInClipboard(text: string, e?: Event | React.MouseEvent<HTMLElement>, onSuccess?: () => void): void {
    if (e) {
      e.preventDefault();
    }

    const script = getScriptForText(text);
    const visibleBounds = !isEditorMobileVariant() ? getVisiblePageBounds(this.page) : null;
    const textItemData = {
      type: ElementDataType.TEXT,
      text: text,
      ...(visibleBounds && {top: visibleBounds.top, left: visibleBounds.left}),
    } as ItemData;
    void this.page.items.addItems.addTextItem(textItemData, {
      textStyles: {
        script: script,
        fontFamily: script ? getRecommendedFont(script) : DEFAULT_TEXT_FONT_FAMILY,
      },
    });
    if (onSuccess) {
      onSuccess();
    }
  }

  public async pasteAudioItemIfInClipboard(data: unknown, e?: Event | React.MouseEvent<HTMLElement>, onSuccess?: () => void): Promise<void> {
    const getAudioItemsDataIfExists = await getAudioItemDataFromClipboard(data);
    if (!getAudioItemsDataIfExists) {
      return;
    }

    if (!this.isPasteAllowed(getAudioItemsDataIfExists, true)) {
      openMessageModal({
        title: window.i18next.t('pmwjs_pasting_error_title'),
        text: window.i18next.t('pmwjs_pasting_error'),
      });
    }

    if (e) {
      e.preventDefault();
    }

    try {
      if (getAudioItemsDataIfExists.audioItemObject) {
        await this.page.items.addItems.addAudioItemFromAudioItemObject(getAudioItemsDataIfExists.audioItemObject);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      console.log(err);
      openMessageModal({
        title: window.i18next.t('pmwjs_pasting_error_title'),
        text: window.i18next.t('pmwjs_pasting_error'),
      });
    }
  }

  public isPasteAllowed(graphicItemsAndPoster: PosterClipboardData, isForAudioItem = false): boolean {
    if (isForAudioItem) {
      return window.PMW.currentUserOnPageLoad?.isSuper || graphicItemsAndPoster.key === this.page.poster.copyKey || !getUserId() || !isUserDesigner();
    }

    return (
      window.PMW.currentUserOnPageLoad?.isSuper ||
      graphicItemsAndPoster.key === this.page.poster.copyKey ||
      !graphicItemsAndPoster.posterInfo.idGalleryTemplate ||
      graphicItemsAndPoster.posterInfo.idGalleryTemplateCreator === getUserId() ||
      !getUserId() ||
      !isUserDesigner()
    );
  }

  public isItemPasteAllowed(copyKey: string, idOriginalOwner?: number, templateCreator?: number): boolean {
    return (
      window.PMW.currentUserOnPageLoad?.isSuper ||
      !getUserId() ||
      !isUserDesigner() ||
      copyKey === this.page.poster.copyKey ||
      idOriginalOwner === getUserId() ||
      templateCreator === getUserId() ||
      !(templateCreator || idOriginalOwner)
    );
  }

  /**
   * Checks if user is allowed to paste multiple items at a time
   */
  public isMultipleItemsPasteAllowed(graphicItemsAndPoster: PosterClipboardData): boolean {
    return (
      window.PMW.currentUserOnPageLoad?.isSuper ||
      graphicItemsAndPoster.key === this.page.poster.copyKey ||
      !graphicItemsAndPoster.posterInfo.idGalleryTemplate ||
      graphicItemsAndPoster.posterInfo.idGalleryTemplateCreator === getUserId()
    );
  }
}

const getDataFromClipboard = async (): Promise<unknown> => {
  let clipText: string;
  let itemsAndPoster;
  try {
    clipText = await readFromClipboard();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'NotAllowedError') {
      showMessageGrowl({
        text: isDesktop
          ? window.i18next.t('pmwjs_clipboard_access_denied_with_shortcut', {shortcut: makeShortcut(['Ctrl', 'V'])})
          : window.i18next.t('pmwjs_clipboard_access_denied'),
        icon: 'icon-poster-print',
        type: GROWL_TYPE.DANGER,
        interval: 2000,
        showCloseIcon: true,
      });
    }
    return undefined;
  }

  try {
    itemsAndPoster = JSON.parse(clipText) as unknown;
  } catch (err) {
    return clipText;
  }

  return itemsAndPoster;
};

const onAfterPastedImageUpload = (type: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse): void => {
  if (!isUserLoggedIn() && !isArray(data)) {
    const mediaItem = getMediaStorageForbackendData(data, type);
    window.PMW.redux.store.dispatch(updateUnlinkedMediaState([mediaItem]));
  }
};

const onPastedImageUploadError = (): void => {
  openErrorModal.bind({message: window.i18next.t('pmwjs_cannot_upload_error_msg')});
};

const getExternalTextDataFromClipboard = async (clipText: string): Promise<string | undefined> => {
  try {
    if (isJson(clipText)) {
      return undefined;
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'NotAllowedError') {
      showMessageGrowl({
        text: isDesktop
          ? window.i18next.t('pmwjs_clipboard_access_denied_with_shortcut', {shortcut: makeShortcut(['Ctrl', 'V'])})
          : window.i18next.t('pmwjs_clipboard_access_denied'),
        icon: 'icon-poster-print',
        type: GROWL_TYPE.DANGER,
        interval: 2000,
        showCloseIcon: true,
      });
      return undefined;
    }
  }

  return clipText;
};

const getItemsAndPosterDataFromClipboard = async (data: unknown): Promise<PosterClipboardData | undefined> => {
  const validateditemsAndPoster = getValidatedItemsAndPosterClipboardData(data);
  if (validateditemsAndPoster) {
    return validateditemsAndPoster;
  }

  return undefined;
};

const getAudioItemDataFromClipboard = async (data: unknown): Promise<PosterClipboardData | undefined> => {
  const validateAudioItemData = getItemsAndPosterClipboardDataForAudio(data);
  if (validateAudioItemData) {
    return validateAudioItemData;
  }

  return undefined;
};

const getExternalTextItemDataFromClipboard = async (data: string): Promise<string | undefined> => {
  const text = await getExternalTextDataFromClipboard(data);

  if (!text || (text && !text.trim())) {
    return undefined;
  }

  return text.trim();
};

export const getItemsAndPosterClipboardDataForAudio = (data: unknown): PosterClipboardData | undefined => {
  if (isObject(data) && 'key' in data && 'version' in data && data.version !== PASTE_ITEMS_VERSION && 'audioItemObject' in data) {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_audio_clip_pasting_error'),
    });
    return undefined;
  }

  if (isObject(data) && 'key' in data && 'audioItemObject' in data && 'posterInfo' in data) {
    return data as PosterClipboardData;
  }
  return undefined;
};

export const getValidatedItemsAndPosterClipboardData = (data: unknown): PosterClipboardData | undefined => {
  if (isObject(data) && 'key' in data && 'version' in data && data.version !== PASTE_ITEMS_VERSION && 'graphicItemObjects' in data) {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_items_pasting_error'),
    });
    return undefined;
  }

  if (isObject(data) && 'key' in data && 'graphicItemObjects' in data && 'posterInfo' in data && 'selection' in data) {
    return data as PosterClipboardData;
  }
  return undefined;
};

export const isItemsAndPosterClipboardDataValid = (data: unknown): boolean => {
  if (!isObject(data) || !('key' in data)) {
    return false;
  }

  if ('version' in data && data.version !== PASTE_ITEMS_VERSION) {
    if ('audioItemObject' in data || 'graphicItemObjects' in data) {
      showMessageGrowl({
        type: GROWL_TYPE.DANGER,
        text: window.i18next.t('audioItemObject' in data ? 'pmwjs_audio_clip_pasting_error' : 'pmwjs_items_pasting_error'),
      });
    }
    return false;
  }

  if ('posterInfo' in data) {
    if ('audioItemObject' in data) {
      return true;
    }
    if ('graphicItemObjects' in data && 'selection' in data) {
      return true;
    }
  }

  return false;
};

// export const isAudioInClipboardData = (data: unknown): boolean => {
//   if (!isObject(data) || !('key' in data)) {
//     return false;
//   }
//   return 'audioItemObject' in data;
// };
